import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { AiImportStatus, GetAiImportStatusQuery, GetAiImportStatusQueryVariables, GetAiImportStatusDocument } from "@/graphql/__generated__/graphql";
import { useAiImportStore } from "@/store/aiImports";
import { useQuery } from "@apollo/client/react";

/**
 * Custom hook to manage polling for AI import status.
 *
 * This hook uses the `useGetAiImportStatusQuery` to poll the AI import status
 * at a specified interval and updates the AI import statuses in the store.
 * Polling can be controlled via the `isPolling` state.
 *
 * @returns {Object} An object containing:
 * - `data`: The data returned from the query.
 * - `loading`: Boolean indicating if the query is currently loading.
 * - `isPolling`: Boolean indicating if polling is active.
 * - `startPolling`: Function to start polling.
 * - `stopPolling`: Function to stop polling.
 * - `setIsPolling`: Function to set the polling state.
 */
export const useAIImportStatusPolling = (): {
  data: GetAiImportStatusQuery | undefined,
  loading: boolean,
  isPolling: boolean,
  startPolling: (_pollInterval: number) => void,
  stopPolling: () => void,
  setIsPolling: Dispatch<SetStateAction<boolean>>
} => {
  const mergeAIImportStatuses = useAiImportStore((state) => state.mergeAIImportStatuses);
  const [isPolling, setIsPolling] = useState(true);

  const { data, loading, stopPolling, startPolling } = useQuery<GetAiImportStatusQuery, GetAiImportStatusQueryVariables>(GetAiImportStatusDocument, {
    pollInterval: 5000,
    skip: !isPolling,  // Control polling with state
    fetchPolicy: "network-only"
  });

  // Helper function to determine if all imports are done (either Success or Failed)
  const areAllImportsDone = (aiImportStatuses: Array<{ status: AiImportStatus }>) => {
    for (const importStatus of aiImportStatuses) {
      if (importStatus.status !== AiImportStatus.Success && importStatus.status !== AiImportStatus.Failed) {
        return false;  // Return early if an in-progress import is found
      }
    }
    return true;  // All imports are done
  };

  // Update store and stop polling when the status data is retrieved
  useEffect(() => {
    if (!loading && data?.getAIImportStatus) {
      mergeAIImportStatuses(data.getAIImportStatus);

      // Stop polling if there are no imports or all imports are done
      // Add a delay to ensure final status updates are captured
      if (data.getAIImportStatus.length === 0 || areAllImportsDone(data.getAIImportStatus)) {
        setTimeout(() => {
          setIsPolling(false);
          stopPolling();  // Stop polling only if necessary
        }, 3000); // 3 second delay to capture final status updates
      }
    }
  }, [data, loading, isPolling, mergeAIImportStatuses, stopPolling]);

  return { data, loading, isPolling, startPolling, stopPolling, setIsPolling };
};
